import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SplashCursor from './SplashCursor';
import { FaWhatsapp, FaTelegramPlane, FaInstagram, FaFacebookF, FaYoutube } from 'react-icons/fa';
import './AboutUs.css';
import './MainPage.css'; // to inherit footer and header styles
import Footer from './Footer';

const AboutUs = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="about-page">
      <SplashCursor RAINBOW_MODE={false} COLOR="#A855F7" />

      {/* Navigation */}
      <header className="header">
        <div className="header-container">
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/images/Final Font Stocks4U1.png" alt="Stocks4U Logo" className="logo-text" style={{ width: '150px', height: 'auto' }} />
          </div>
          
          <nav className={`nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
            <a href="/" onClick={() => setIsMobileMenuOpen(false)}>Home</a>
            <a href="/#features" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
            <a href="/#pricing" onClick={() => setIsMobileMenuOpen(false)}>Pricing</a>
            <a href="/about" className="active" onClick={() => setIsMobileMenuOpen(false)}>About</a>
            <a href="/#contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
          </nav>

          <button
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          <div className="header-buttons">
            <button className="login-btn" onClick={() => navigate('/login')}>Login</button>
            <button className="get-started-btn" onClick={() => navigate('/login')}>Get Started</button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="about-hero-new">
        <div className="about-hero-bg-glow"></div>
        <div className="about-hero-content">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-badge"
          >
            FINTECH REVOLUTION
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="about-title-hero"
          >
            We Build <br/> <span className="gradient-text-hero">Trading Intelligence</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="about-hero-desc"
          >
            Stocks4U empowers modern traders with institutional-grade AI analytics, bringing unprecedented speed and precision to the retail market.
          </motion.p>
        </div>
      </section>

      {/* Expertise Section (What We Do) */}
      <section className="about-expertise">
        <div className="container">
          <div className="expertise-header">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              WHAT WE DO?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              We provide the edge you need to stay ahead of dynamic markets. Our proprietary algorithms decode market noise into actionable signals.
            </motion.p>
          </div>

          <div className="expertise-grid">
            {[
              {
                title: 'Real-Time Insights',
                desc: 'Harness the power of AI to analyze thousands of data points instantly, revealing hidden market momentum and sector rotation patterns.',
                icon: '⚡'
              },
              {
                title: 'Institutional Edge',
                desc: 'Access advanced tools like FII/DII data tracking, Index Movers, and Options Heatmaps previously reserved for large financial institutions.',
                icon: '📈'
              },
              {
                title: 'Precision Execution',
                desc: 'Execute trades with confidence using our Intraday Scanners, Swing Zones, and Momentum Radars to identify exact entry and exit points.',
                icon: '🎯'
              },
              {
                title: 'Educational Growth',
                desc: 'We don’t just give you tools; we teach you how to use them. Our curated tutorials and resources turn beginners into seasoned pros.',
                icon: '🧠'
              }
            ].map((feature, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="expertise-card"
              >
                <div className="expertise-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
                <div className="expertise-card-glow"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Identity Section (Who Are We) */}
      <section className="about-identity">
        <div className="container identity-container">
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="identity-text"
          >
            <h2>WHO ARE WE?</h2>
            <p>
              Born from a passion for democratizing finance, Stocks4U is a collective of visionary developers, quantitative analysts, and seasoned traders. 
              We recognized a massive gap between the tools available to Wall Street and those accessible to everyday investors.
            </p>
            <p>
              Our mission is simple: level the playing field. By combining cutting-edge technology, sleek user-centric design, and deep financial intelligence, we are building an ecosystem where anyone can analyze, adapt, and conquer the stock market.
            </p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="identity-visual"
          >
             <div className="abstract-sphere"></div>
             <div className="abstract-ring"></div>
             <div className="abstract-ring-2"></div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutUs;
